import { useEffect, useState } from 'react';
import { getApiUrl } from './api/public';

interface Version {
    version: string;
    buildTimestamp: string;
}

export function useVersionCheck(intervalMs = 60000, autoReload = false) {
    const [needsRefresh, setNeedsRefresh] = useState(false);
    const [currentVersion, setCurrentVersion] = useState<Version | null>(null);
    const [newVersion, setNewVersion] = useState<Version | null>(null);

    useEffect(() => {
        let initialVersion: Version | null = null;

        const checkVersion = async () => {
            try {
                const cacheBuster = Date.now();
                const versionUrl = `${getApiUrl()}/version?t=${cacheBuster}`.replace(/([^:])\/\/+/g, '$1/');
                const [apiResponse, buildResponse] = await Promise.all([
                    fetch(versionUrl, { cache: 'no-store', credentials: 'omit' }),
                    fetch(`/version.json?t=${cacheBuster}`, { cache: 'no-store' }),
                ]);
                if (!apiResponse.ok || !buildResponse.ok) {
                    throw new Error('Failed to check for new version');
                }

                const apiVersion = (await apiResponse.json()) as { version?: string };
                const buildVersion: Version = await buildResponse.json();

                if (!apiVersion.version) {
                    throw new Error('Failed to check for new version');
                }

                const version: Version = {
                    version: apiVersion.version,
                    buildTimestamp: buildVersion.buildTimestamp,
                };

                if (!initialVersion) {
                    initialVersion = version;
                    setCurrentVersion(version);
                    return;
                }

                if (version.buildTimestamp !== initialVersion.buildTimestamp ||
                    version.version !== initialVersion.version) {
                    if (autoReload) {
                        window.location.reload();
                    } else {
                        setNeedsRefresh(true);
                        setNewVersion(version);
                    }
                }
            } catch (error) {
                console.error('Failed to check for new version:', error);
            }
        };

        checkVersion();
        const interval = setInterval(checkVersion, intervalMs);
        return () => clearInterval(interval);
    }, [intervalMs, autoReload]);

    return {
        needsRefresh,
        currentVersion,
        newVersion,
        refreshPage: () => window.location.reload()
    };
}